import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScanLine, FileText, Sparkles, TrendingUp, MapPin, ShieldCheck, ArrowRight, CheckCircle2, X, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  "Parsing your experience…",
  "Extracting skills & certifications…",
  "Matching against live shipyard jobs…",
  "Mapping your career pathway…",
  "Calculating earning potential…",
  "Writing your personalized report…",
];

export function ScanningOverlay({ active }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!active) { setStep(0); return; }
    const id = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 1600);
    return () => clearInterval(id);
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="scan-overlay"
          className="fixed inset-0 z-[100] bg-[#0B0F14]/95 backdrop-blur-md grid place-items-center px-6"
        >
          <div className="absolute inset-0 blueprint-grid opacity-20" />
          <div className="relative w-full max-w-md text-center">
            <div className="font-mono-tech text-[11px] tracking-[0.3em] text-[#FF5E1E] mb-6">[ REENTRY AI · RESUME ANALYSIS ]</div>

            {/* Scan doc */}
            <div className="relative mx-auto w-44 h-56 bg-[#10161F] border border-[#FF5E1E]/30 overflow-hidden shadow-[0_0_60px_rgba(255,94,30,0.25)]">
              <div className="p-5 space-y-2.5 opacity-60">
                <FileText className="text-[#FF5E1E] mb-2" size={22} />
                {[90, 70, 80, 55, 75, 60, 85, 50].map((w, i) => (
                  <div key={i} className="h-1.5 bg-white/15" style={{ width: `${w}%` }} />
                ))}
              </div>
              <div className="scan-line" />
              <div className="absolute inset-0 border-t-2 border-[#FF5E1E]/0" />
            </div>

            <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-8 flex items-center justify-center gap-2 text-[#F4F6F8]">
              <ScanLine size={16} className="text-[#FF5E1E] animate-pulse" />
              <span className="font-display font-bold uppercase tracking-wide text-sm" data-testid="scan-step">{STEPS[step]}</span>
            </motion.div>

            <div className="mt-5 h-1 w-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#FF5E1E] to-[#FFB800] scan-progress" />
            </div>
            <p className="mt-5 font-mono-tech text-[10px] tracking-[0.18em] text-[#8FA3B8]">ANALYZING WITH REENTRY AI · THIS TAKES A FEW SECONDS</p>
          </div>

          <style>{`
            .scan-line { position:absolute; left:0; right:0; height:2px; background:linear-gradient(90deg,transparent,#FF5E1E,transparent); box-shadow:0 0 16px 3px rgba(255,94,30,0.7); animation:scanmove 2s ease-in-out infinite; }
            @keyframes scanmove { 0%{top:2%} 50%{top:94%} 100%{top:2%} }
            .scan-progress { width:20%; animation:scanprog 1.6s ease-in-out infinite; }
            @keyframes scanprog { 0%{width:8%;margin-left:0} 50%{width:70%} 100%{width:96%;margin-left:4%} }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ScanResultModal({ open, result, firstName, onClose, onDashboard }) {
  if (!open || !result) return null;
  const fit = result.fit_score ?? null;
  const jobs = result.matched_jobs || [];
  const earn = result.potential_earnings || {};
  const path = result.career_path || [];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-4" data-testid="scan-result-modal">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
          className="relative mx-auto max-w-2xl bg-[#10161F] border border-[#FF5E1E]/30 ember-glow"
        >
          <button data-testid="scan-result-close" onClick={onClose} className="absolute top-4 right-4 text-[#8FA3B8] hover:text-white z-10"><X size={20} /></button>

          {/* Header */}
          <div className="p-6 sm:p-8 border-b border-white/10 bg-gradient-to-br from-[#FF5E1E]/10 to-transparent">
            <div className="flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3"><Sparkles size={14} /> YOUR REENTRY AI CAREER REPORT</div>
            <h3 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-none">{result.headline || "Your shipyard path is ready"}</h3>
            <div className="mt-5 flex flex-wrap items-center gap-5">
              {fit != null && (
                <div className="flex items-center gap-3" data-testid="scan-fit-score">
                  <div className="relative h-16 w-16 grid place-items-center">
                    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" /><circle cx="18" cy="18" r="16" fill="none" stroke="#FF5E1E" strokeWidth="3" strokeDasharray={`${(fit / 100) * 100} 100`} strokeLinecap="round" /></svg>
                    <span className="font-display font-black text-xl">{fit}</span>
                  </div>
                  <div className="font-mono-tech text-[10px] tracking-[0.15em] text-[#8FA3B8] uppercase leading-tight">Workforce<br />Fit Score</div>
                </div>
              )}
              {result.top_trade && (
                <div>
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#8FA3B8] uppercase">Best-fit trade</div>
                  <div className="font-display font-black uppercase text-2xl text-[#FFB800]">{result.top_trade}</div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-7">
            {/* Personalized message */}
            {result.personalized_message && (
              <div className="border-l-2 border-[#FF5E1E] pl-4" data-testid="scan-message">
                <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#FF5E1E] uppercase mb-2">A note for you{firstName ? `, ${firstName}` : ""}</div>
                <p className="text-[#E4EAF0] leading-relaxed text-[15px]">{result.personalized_message}</p>
              </div>
            )}

            {/* Matched jobs */}
            {jobs.length > 0 && (
              <div>
                <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.2em] text-white uppercase mb-3"><Compass size={14} className="text-[#FF5E1E]" /> Your best-matching jobs</div>
                <div className="space-y-2.5">
                  {jobs.map((j, i) => (
                    <div key={i} data-testid={`scan-job-${i}`} className="bg-[#0B0F14] border border-white/10 hover:border-[#FF5E1E]/40 p-4 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-display font-bold uppercase">{j.title}</div>
                        <div className="text-[#10B981] font-medium text-sm whitespace-nowrap">{j.pay}</div>
                      </div>
                      <div className="flex items-center gap-3 mt-1 font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] uppercase">
                        <span>{j.trade}</span>{j.location && <span className="flex items-center gap-1"><MapPin size={10} className="text-[#FF5E1E]" /> {j.location}</span>}
                      </div>
                      {j.reason && <p className="mt-2 text-sm text-[#B8C4D0]">{j.reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Earnings */}
            {(earn.entry || earn.experienced || earn.top) && (
              <div>
                <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.2em] text-white uppercase mb-3"><TrendingUp size={14} className="text-[#FF5E1E]" /> Your earning potential</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["Entry", earn.entry], ["Experienced", earn.experienced], ["Top", earn.top]].map(([l, v]) => (
                    <div key={l} className="bg-[#0B0F14] border border-white/10 p-3 text-center">
                      <div className="font-display font-black text-lg text-[#10B981]">{v || "—"}</div>
                      <div className="font-mono-tech text-[8px] tracking-[0.12em] text-[#8FA3B8] uppercase mt-1">{l}</div>
                    </div>
                  ))}
                </div>
                {earn.annual_top && <p className="mt-2 text-center font-mono-tech text-[10px] tracking-[0.15em] text-[#FFB800]">UP TO {earn.annual_top} AT THE TOP OF THIS TRADE</p>}
              </div>
            )}

            {/* Path */}
            {path.length > 0 && (
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.2em] text-white uppercase mb-3">Your pathway</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {path.map((p, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="bg-[#182230] border border-white/10 px-3 py-1.5">
                        <div className="font-display font-bold uppercase text-xs">{p.role}</div>
                        <div className="font-mono-tech text-[8px] text-[#8FA3B8]">{p.pay_range}</div>
                      </div>
                      {i < path.length - 1 && <ArrowRight size={12} className="text-[#FF5E1E]" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.gaps?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase self-center">Next steps:</span>
                {result.gaps.map((g, i) => <span key={i} className="font-mono-tech text-[9px] tracking-[0.1em] text-[#FFB800] border border-[#FFB800]/30 px-2 py-1 flex items-center gap-1"><CheckCircle2 size={10} /> {g}</span>)}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button data-testid="scan-view-dashboard" onClick={onDashboard} className="flex-1 rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-12 group">
                View My Passport <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
              </Button>
              <Button asChild variant="outline" className="rounded-none border-white/20 bg-transparent hover:bg-white/10 text-white font-display font-bold uppercase h-12">
                <a href="#jobs" onClick={onClose}>Explore Jobs</a>
              </Button>
            </div>
            <p className="flex items-center justify-center gap-2 text-[10px] text-[#8FA3B8] font-mono-tech tracking-wide text-center"><ShieldCheck size={12} className="text-[#10B981]" /> AI GUIDANCE ONLY · NO CERTIFICATION, ACCESS, OR EMPLOYMENT IS GUARANTEED.</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
